
import { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '@/contexts/auth';
import { supabase } from '@/lib/supabase/client';

/**
 * Hook for checking if the current user has admin access
 * Uses server-side RPC calls for secure role verification
 * Now uses authResolved to prevent permanent loading states
 */
export function useAdminCheck() {
  const { user, isAuthenticated, authResolved, hasSession } = useAuthContext();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const checkRoles = useCallback(async () => {
    // 🛡️ Wait for auth to resolve before making any decision
    if (!authResolved) {
      return; // Keep loading=true until auth is resolved
    }
    
    // Auth resolved but no session → not admin, stop loading
    if (!hasSession) {
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setLoading(false);
      return;
    }
    
    try {
      // RPC calls with 5s timeout to prevent infinite loading
      const rpcWithTimeout = async (rpcCall: ReturnType<typeof supabase.rpc>) => {
        const result = await Promise.race([
          rpcCall,
          new Promise<null>(resolve => setTimeout(() => resolve(null), 5000))
        ]);
        return result;
      };
      
      const [adminResult, superAdminResult] = await Promise.all([
        rpcWithTimeout(supabase.rpc('is_admin')),
        rpcWithTimeout(supabase.rpc('is_super_admin'))
      ]);
      
      setIsAdmin(adminResult && typeof adminResult === 'object' && 'data' in adminResult ? (adminResult as any).data === true : false);
      setIsSuperAdmin(superAdminResult && typeof superAdminResult === 'object' && 'data' in superAdminResult ? (superAdminResult as any).data === true : false);
    } catch (error) {
      console.error('Error checking admin roles via RPC:', error);
      setIsAdmin(false);
      setIsSuperAdmin(false);
    } finally {
      setLoading(false);
    }
  }, [authResolved, hasSession]);
  
  useEffect(() => {
    checkRoles();
  }, [checkRoles]);
  
  // Fast path: if auth resolved and no session, return immediately without loading
  if (authResolved && !hasSession) {
    return { isAdmin: false, isSuperAdmin: false, loading: false };
  }
  
  return { isAdmin, isSuperAdmin, loading };
}
